import { formatAppCurrency } from '../lib/currency';
import { ReportDateRange, ReportMetricTone } from '../types/reports';

const SHORT_DATE_FORMATTER = new Intl.DateTimeFormat('en-UG', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat('en-UG', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export const formatReportMoney = (value: number): string =>
  formatAppCurrency(Math.round(value || 0));

export const parseReportDate = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const formatReportDateValue = (value: Date): string => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const addReportDays = (value: Date, days: number): Date =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate() + days);

export const formatReportDate = (value: string): string => {
  const parsed = parseReportDate(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return SHORT_DATE_FORMATTER.format(parsed);
};

export const formatReportDateTime = (value: string): string => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return DATE_TIME_FORMATTER.format(parsed);
};

export const formatReportUserName = (
  firstName?: string | null,
  lastName?: string | null,
): string => {
  const parts = [firstName, lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : 'System user';
};

export const formatReportMode = (value?: string | null): string => {
  if (!value) return 'Unknown';
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const formatReportRole = (value?: string | null): string => {
  if (!value) return 'Unknown role';
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const getDefaultReportDateRange = (
  referenceDate?: string | null,
): ReportDateRange => {
  const endDate =
    referenceDate && !Number.isNaN(parseReportDate(referenceDate).getTime())
      ? referenceDate
      : formatReportDateValue(new Date());

  return {
    endDate,
    startDate: formatReportDateValue(
      addReportDays(parseReportDate(endDate), -29),
    ),
  };
};

export const getReportMetricToneClasses = (
  tone: ReportMetricTone = 'slate',
): string => {
  switch (tone) {
    case 'emerald':
      return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    case 'blue':
      return 'bg-sky-50 text-sky-700 border-sky-100';
    case 'amber':
      return 'bg-amber-50 text-amber-700 border-amber-100';
    case 'rose':
      return 'bg-rose-50 text-rose-700 border-rose-100';
    case 'slate':
    default:
      return 'bg-slate-50 text-slate-700 border-slate-100';
  }
};

export const getReportAlignmentClass = (
  align?: 'left' | 'center' | 'right',
) => {
  switch (align) {
    case 'center':
      return 'text-center';
    case 'right':
      return 'text-right';
    case 'left':
    default:
      return 'text-left';
  }
};
